import { Coordinates } from "models";

export interface DocScanFarm {
  id: string;
  name: string;
  country: string | null;
}

export interface DocScanUser {
  id: string;
  firstName: string;
  lastName: string;
}

export type DocScanType = "extract" | "match";

export type DocScanSource = "manualUpload" | "verificationTaskUpload" | "backfill";

export interface DocScanJob {
  id: string;
  status: string;
  type: DocScanType;
  year: number | null;
  score: number | null;
  overallStatus: string | null;
  createdAt: string;
  dispatchedAt?: string | null;
  completedAt: string | null;
  source: DocScanSource;
  isAdmin: boolean;
  dispatchAttempts: number;
  farm: DocScanFarm;
  user?: DocScanUser | null;
  adminUser?: DocScanUser | null;
}

export type CreateDocScanResponse = Omit<DocScanJob, "farm" | "user" | "adminUser"> & {
  fileName: string | null;
  fileHash: string | null;
  // true when the API returns a completed scan of the same document and does not start a new job
  isReused: boolean;
};

export interface GetDocScansResponse {
  jobs: DocScanJob[];
  count: number;
  maxDispatchAttempts: number;
}

export type DocScanSortBy = "createdAt" | "updatedAt" | "completedAt" | "status" | "id";
export type DocScanSortOrder = "asc" | "desc";

// Details

export type FarmDocScanMatchData = {
  final_match_results: DocScanFinalMatchResult;
  programmatic_pair_results: DocScanProgrammaticPairResult;
};

export type FarmDocScanExtractData = {
  fields: DocFieldRaw[];
};

export const DOC_SCAN_ERROR_CODE = {
  YEAR_MISMATCH: "YEAR_MISMATCH",
} as const;

export type DocScanErrorCode = (typeof DOC_SCAN_ERROR_CODE)[keyof typeof DOC_SCAN_ERROR_CODE];

export type DocScanValidationError = {
  error: { code: typeof DOC_SCAN_ERROR_CODE.YEAR_MISMATCH; message: string; expected_year: number; document_year: number };
};

export type FarmDocScanJobData = FarmDocScanMatchData | FarmDocScanExtractData;

export interface DocScanMatchedByGroup {
  matchedBy: string;
  count: number;
  percentage: number;
}

export interface DocScanOverallStatusGroup {
  overallStatus: string;
  count: number;
}

export interface DocScanReviewStats {
  reviewed: number;
  correct: number;
  incorrect: number;
  truePositive: number;
  trueNegative: number;
  falsePositive: number;
  falseNegative: number;
}

export interface DocScanStatistics {
  total: number;
  inDb: number;
  notInDb: number;
  matchedByGroups: DocScanMatchedByGroup[];
  overallStatusGroups: DocScanOverallStatusGroup[];
  review: DocScanReviewStats;
}

export interface DocScanJobDetail {
  id: string;
  type: DocScanType;
  userId: string;
  farmId: string;
  filePath: string;
  /** Short-lived presigned URL of the uploaded PDF. Null for local-runtime scans, which are not in S3. */
  fileUrl?: string | null;
  source: DocScanSource;
  isAdmin: boolean;
  user: DocScanUser | null;
  dispatchAttempts: number;
  maxDispatchAttempts: number;
  status: string;
  year: number | null;
  score: number | null;
  overallStatus: string | null;
  data: FarmDocScanJobData | RunContext | FarmDocScanExtractData | DocScanValidationError | null;
  statistics?: DocScanStatistics | null;
  completedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface DocScanFinalMatchResult {
  overall_status: string;
  overall_score: number;
  fields_score: number;
  region_status: string;
  address_status: string;
  farm_details_differences: FarmDetailsDifference[];
  field_matches: FieldMatch[];
  summary: string;
}

export interface FarmDetailsDifference {
  attribute_name: string;
  status: string;
  db_value: string;
  doc_value: string;
  reason: string;
}

export interface FieldMatch {
  farm_field_id: string;
  matched_by: string;
  doc_field_name: string;
  status: string;
  score: number;
  crops_score: number;
  flik_status: string;
  field_number_status: string;
  field_name_status: string;
  field_size_status: string;
  flik_score: number;
  field_number_score: number;
  field_name_score: number;
  field_size_score: number;
  differences: Difference[];
  crop_matches: CropMatch[];
}

export interface Difference {
  attribute_name: string;
  status: string;
  db_value: string;
  doc_value: string;
  reason: string;
}

export interface CropMatch {
  doc_crop_name: string;
  status: string;
  score: number;
  crop_name_score: number;
  crop_year_score: number;
  crop_name_status: string;
  crop_year_status: string;
  differences: Difference[];
}

export interface DocScanProgrammaticPairResult {
  run_context: RunContext;
  fields: DocScanField[];
}

export interface RunContext {
  farm_id: string;
  document_year: number;
  overall_score: number;
  overall_status: string;
  fields_score: number;
  region_status: string;
  region_score?: number;
  address_status: string;
  address_score?: number;
  summary: string;
  ocr_engine?: string | null;
}

export interface DocScanField {
  farm_field_id?: string;
  farm_field_raw?: FarmFieldRaw;
  doc_field_raw: DocFieldRaw;
  scores: Scores;
  statuses: Statuses;
  differences: Difference[];
  crops: CropMatchResult[];
}

export interface FarmFieldRaw {
  id: string;
  name: string;
  number: string;
  is_split: boolean;
  root_field_id: string | null;
  flik: string;
  size: number;
  location: string;
  crops: DBCrop[];
}

export interface DBCrop {
  id: string;
  year: number;
  seeding_start: string;
  seeding_end: string;
  harvest_start: string;
  harvest_end: string;
  verification_year: number | null;
  crop_name: string;
  crop_code: string | null;
}

export interface DocFieldRaw {
  field_number: string;
  field_name: string;
  flik_number: string;
  field_size: string;
  crops: DocCrop[];
  /** Populated for PL only — the matched Polish cadastral (registry) parcel. */
  geometry?: DocParcelGeometry | null;
  /**
   * PL only. Set when one platform field holds several document parcels: this field is then a
   * synthetic group (its own boundary is empty) and the real parcels are listed here, each with
   * its own boundary and crops.
   */
  grouped_doc_fields?: DocFieldRaw[] | null;
  /** Shared by a group and its parcels (the platform field id). */
  split_group_id?: string | null;
  /** On a group, true when its parcels declare different crops, so the field must be split first. */
  is_pre_split_parent?: boolean | null;
  /** Where doc-scan read this field on the source PDF. A group holds its parcels' locations. */
  source_locations?: SourceLocation[] | null;
}

export interface DocParcelGeometry {
  parcel_id: string;
  source: "uldk" | "egib" | "both";
  /** Registry parcel boundary, WGS84, ready to render (mirrors FieldShape.geometry). */
  boundary_wgs84?: { lat: number; lng: number }[] | null;
  area_ha?: number | null;
  province?: string | null;
  region?: string | null;
  municipality?: string | null;
}

export interface DocCrop {
  crop_code: string | null;
  crop_name: string;
  crop_size: string;
  crop_year: number | null;
  source_locations?: SourceLocation[] | null;
}

/** Box on the source page, 0-1 of the page width and height, origin top left, page rotation applied. */
export interface SourceBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export type SourceConfidence = "high" | "multi" | "low" | "page_only";

export interface SourceLocation {
  /** 1-based page in the original uploaded PDF. */
  page: number;
  /** How the row was found. Null when the locator failed and only the page is known. */
  method?: "pdf_table" | "pdf_lines" | "textract" | "textract_lines" | null;
  /**
   * high: one row. multi: one of several rows with the same PL parcel. low: a possible row
   * without the field ID. page_only: no row found, only the page.
   */
  confidence: SourceConfidence;
  /** PL only: "parcel" for a Section XI row, "crop" for a Section XII row. */
  section?: "parcel" | "crop" | null;
  row?: SourceBox | null;
  /** Cell box per matched attribute, keyed like Difference.attribute_name (flik, field_size, crop_name, ...). */
  cells: Record<string, SourceBox>;
}

export interface Scores {
  flik_score?: number;
  field_number_score?: number;
  field_name_score?: number;
  field_size_score?: number;
  crops_score?: number;
  geometry_score?: number | null;
  field_score?: number;
}

export interface Statuses {
  overall_status: string;
  flik_status?: string;
  field_number_status?: string;
  field_name_status?: string;
  field_size_status?: string;
  crops_status?: string;
  geometry_status?: string | null;
}

export interface CropMatchResult {
  doc_crop_name: string;
  status: string;
  score: number;
  crop_name_score: number;
  crop_year_score: number;
  crop_name_status: string;
  crop_year_status: string;
  differences: Difference[];
}

// Fields list API

export type DocScanFieldSortBy = "createdAt" | "updatedAt" | "fieldScore" | "overallStatus";

export interface DocScanFieldRecord {
  id: string;
  farmDocumentScanId: string;
  fieldId: string | null;
  matchedBy: string | null;
  flikScore: number | null;
  fieldNumberScore: number | null;
  fieldNameScore: number | null;
  fieldSizeScore: number | null;
  cropsScore: number | null;
  fieldScore: number | null;
  geometryScore: number | null;
  overallStatus: string | null;
  statuses: Statuses | null;
  farmFieldRaw: FarmFieldRaw | null;
  docFieldRaw: DocFieldRaw | null;
  differences: Difference[] | null;
  crops: CropMatchResult[] | null;
  comment: string | null;
  commentedAt: string | null;
  isCorrect: boolean | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListDocScanFieldsResponse {
  fields: DocScanFieldRecord[];
  count: number;
}

export interface DocScanGlobalStatistics extends DocScanStatistics {
  totalFarms: number;
}

export const toDocScanField = (r: DocScanFieldRecord): DocScanField => ({
  farm_field_id: r.fieldId ?? undefined,
  farm_field_raw: r.farmFieldRaw ?? undefined,
  doc_field_raw: r.docFieldRaw!,
  scores: {
    flik_score: r.flikScore ?? undefined,
    field_number_score: r.fieldNumberScore ?? undefined,
    field_name_score: r.fieldNameScore ?? undefined,
    field_size_score: r.fieldSizeScore ?? undefined,
    crops_score: r.cropsScore ?? undefined,
    geometry_score: r.geometryScore,
    field_score: r.fieldScore ?? undefined,
  },
  statuses: r.statuses!,
  differences: r.differences ?? [],
  crops: r.crops ?? [],
});

// Verification hub

export type DocScanMatchStatus = "match" | "mismatch";

export type DocScanSizeStatus = "ok" | "excessive";

export interface DocScanFieldResult {
  scanFieldId: string;
  fieldId: string;
  year: number;
  // mismatch when the crop or the size is off, null when neither can be compared
  status: DocScanMatchStatus | null;
  cropStatus: DocScanMatchStatus | null;
  sizeStatus: DocScanSizeStatus | null;
  // signed % of the platform size, 2 decimals
  sizeVariancePct: number | null;
  // false when an admin marked the match wrong, null before a review
  isCorrect: boolean | null;
}

export interface GetFarmDocScanFieldsResponse {
  fields: DocScanFieldResult[];
}

export interface DocScanYearSummary {
  year: number;
  docs: number;
  matchedPct: number;
  cropOk: number;
  cropWrong: number;
  sizeDiff: number;
  splits: number;
}

export interface GetFarmDocScanSummaryResponse {
  years: DocScanYearSummary[];
}

export type DocScanConfidence = "high" | "medium" | "low";

// platform is the field now, platformAtScan the field doc-scan compared against
export interface DocScanTextValues {
  doc: string | null;
  platform: string | null;
  platformAtScan: string | null;
}

export interface DocScanCropValue {
  code: string | null;
  name: string | null;
}

export interface DocScanCropValues {
  doc: DocScanCropValue | null;
  platform: DocScanCropValue | null;
  platformAtScan: DocScanCropValue | null;
}

export interface DocScanSizeValues {
  doc: number | null;
  platform: number | null;
  platformAtScan: number | null;
  maxAllowedDeviationPct: number | null;
}

// PL only: CAP parcels are matched by shape and location
export interface DocScanOverlap {
  overlapPct: number | null;
  location: boolean;
  docShapes: Coordinates[][];
  platformShape: Coordinates[] | null;
  platformShapeAtScan: Coordinates[] | null;
}

export interface GetFarmDocScanFieldResponse extends DocScanFieldResult {
  scanId: string;
  fileName: string | null;
  // short-lived link to the PDF, null when the file is not in S3
  fileUrl: string | null;
  confidence: DocScanConfidence;
  farmerConfirmed: boolean;
  identifiers: { fieldNumber: boolean; flik: boolean; fieldName: boolean; size: boolean; mainCrop: boolean };
  comparison: {
    mainCrop: DocScanCropValues;
    fieldSize: DocScanSizeValues;
    fieldNumber: DocScanTextValues;
    fieldName: DocScanTextValues;
    flik: DocScanTextValues;
    requiresSplit: boolean;
  };
  overlap: DocScanOverlap | null;
  sourceLocations: SourceLocation[];
  review: { isCorrect: boolean | null; comment: string | null };
}
